'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const stats = [
{ value: 500, suffix: '+', label: 'Fortune 500 Clients' },
{ value: 15, suffix: 'K+', label: 'Placements Made' },
{ value: 98, suffix: '%', label: 'Client Satisfaction' },
{ value: 20, suffix: '+', label: 'Years of Excellence' }];


const services = [
{
  icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  title: 'IT Staffing Solutions',
  description: 'We source, screen, and place top-tier IT professionals across all technology domains — from software engineers to cloud architects.'
},
{
  icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  title: 'Executive Search',
  description: 'Identify and recruit C-suite and senior technology leaders who drive digital transformation and innovation at the highest levels.'
},
{
  icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  title: 'Contract Staffing',
  description: 'Flexible contract and contract-to-hire solutions that scale with your business needs, providing agility without compromising quality.'
},
{
  icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
  title: 'IT Consulting',
  description: 'Strategic technology consulting to help Fortune 500 companies navigate complex digital transformations and technology challenges.'
}];


const caseStudies = [
{
  title: 'Scaling Cloud Migration Team: 0 to 45 Engineers in 60 Days',
  industry: 'Financial Services',
  excerpt: 'A major financial services company committed to migrating 300+ legacy applications to AWS. Their existing vendors failed to deliver. TranceformRIGHT deployed 45 qualified engineers in 60 days.',
  result: '$2.4M saved vs. consulting firm rates'
},
{
  title: 'Rebuilding Healthcare IT Department Through Full-Cycle RPO',
  industry: 'Healthcare',
  excerpt: 'Following a merger, a national healthcare network needed to consolidate two IT departments and hire 120 net-new technology roles within 9 months while maintaining HIPAA compliance.',
  result: '64% reduction in time-to-hire'
},
{
  title: 'Centralizing Contingent Workforce Across 12 Vendors',
  industry: 'Manufacturing',
  excerpt: 'A global technology manufacturer managing 400+ contingent workers across 12 vendors with no centralized visibility. TranceformRIGHT implemented a full MSP program.',
  result: '$1.8M annual cost savings'
}];


const newsItems = [
{
  category: 'News',
  title: 'TranceformRIGHT Expands Healthcare IT Staffing Practice to Meet Growing Demand',
  excerpt: 'TranceformRIGHT today announced the expansion of its Healthcare IT staffing practice, adding specialized recruiters and a dedicated talent pipeline for Epic, Cerner, and clinical data professionals...',
  date: 'January 15, 2024'
},
{
  category: 'News',
  title: 'TranceformRIGHT Named Top IT Staffing Firm for Third Consecutive Year',
  excerpt: 'For the third year running, TranceformRIGHT has been recognized as a top IT staffing firm by Staffing Industry Analysts, citing exceptional client satisfaction and placement quality...',
  date: 'December 8, 2023'
},
{
  category: 'News',
  title: 'New Global Payroll and Employer of Record Services Launched for International Expansion',
  excerpt: 'TranceformRIGHT has launched comprehensive Employer of Record (EOR) and Global Payroll services, enabling clients to hire compliantly in 40+ countries without establishing local entities...',
  date: 'November 20, 2023'
}];


function AnimatedCounter({ target, suffix = '' }: {target: number;suffix?: string;}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const duration = 2000;
          const step = target / duration * 16;
          const timer = setInterval(() => {
            start += step;
            if (start >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{count}{suffix}</span>;
}

export default function HomePage() {
  return (
    <div className="min-h-screen" style={{ background: '#ffffff' }}>
      <Navbar />

      {/* Hero Section - Pinnacle1 style: dark navy bg with large PG-style graphic, thin Poppins heading */}
      <section
        className="relative flex items-center overflow-hidden"
        style={{
          paddingTop: '144px',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #01142D 0%, #0A1F3D 50%, #1F65AB 100%)'
        }}>
        
        {/* Background image overlay */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1920&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15
          }} />
        

        {/* Large background monogram - Pinnacle1 style */}
        <div
          className="absolute left-0 bottom-0 z-0 select-none pointer-events-none"
          style={{
            fontSize: '480px',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            color: 'rgba(0,167,255,0.06)',
            lineHeight: 1,
            letterSpacing: '-0.05em'
          }}>
          
          TR
        </div>

        {/* Radial glow */}
        <div
          className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(0,167,255,0.15) 0%, transparent 70%)' }} />
        

        <div className="container-custom relative z-10 py-20">
          <div className="max-w-3xl">
            {/* Eyebrow */}
            <p
              className="mb-6"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '13px',
                fontWeight: 600,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: '#00A7FF'
              }}>
              
              Trusted by Fortune 500 Companies
            </p>

            {/* H1 - Pinnacle1 style: weight 200, large, white */}
            <h1
              className="mb-4"
              suppressHydrationWarning
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: 'clamp(40px, 6vw, 72px)',
                fontWeight: 200,
                lineHeight: 1.2,
                color: '#ffffff',
                letterSpacing: '-0.01em'
              }}>Transformation, Growth, Reinvention


            </h1>

            {/* H3 - Pinnacle1 style: weight 100 */}
            <h3
              className="mb-6"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: 'clamp(22px, 3.5vw, 40px)',
                fontWeight: 100,
                lineHeight: 1.3,
                color: '#ffffff'
              }}>
              
              Premier IT Staffing &amp; Consulting Partner for Fortune 500.
            </h3>

            {/* Divider - Pinnacle1 style */}
            <div style={{ width: '80px', height: '2px', background: '#00A7FF', marginBottom: '32px' }}></div>

            {/* Service quick links - Pinnacle1 style: H5 white links */}
            <div className="flex flex-wrap gap-x-8 gap-y-3 mb-10">
              {['IT Staffing Solutions', 'Executive Search', 'Contract Staffing', 'IT Consulting'].map((service) =>
              <Link
                key={service}
                href="/services"
                className="transition-colors duration-200"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '18px',
                  fontWeight: 400,
                  color: '#ffffff'
                }}
                onMouseEnter={(e) => {(e.currentTarget as HTMLElement).style.color = '#00A7FF';}}
                onMouseLeave={(e) => {(e.currentTarget as HTMLElement).style.color = '#ffffff';}}>
                
                  {service}
                </Link>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-white font-semibold transition-all duration-300 hover:opacity-90"
                style={{ background: '#00A7FF', fontFamily: 'Montserrat, sans-serif', fontSize: '15px' }}>
                
                Find Top Talent
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                href="/services"
                className="inline-flex items-center justify-center px-8 py-4 font-semibold transition-all duration-300"
                style={{ border: '2px solid rgba(255,255,255,0.5)', color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontSize: '15px' }}
                onMouseEnter={(e) => {(e.currentTarget as HTMLElement).style.borderColor = '#00A7FF';(e.currentTarget as HTMLElement).style.color = '#00A7FF';}}
                onMouseLeave={(e) => {(e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.5)';(e.currentTarget as HTMLElement).style.color = '#ffffff';}}>
                
                Explore Services
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce z-20">
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontFamily: 'Montserrat, sans-serif' }}>Scroll Down</span>
          <svg className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.4)' }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </section>

      {/* Services Section - Pinnacle1 style: white bg, image+text alternating layout */}
      <section className="section-padding" style={{ background: '#ffffff' }}>
        <div className="container-custom">
          {/* Strategic Services Block */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              {/* H6 label - Pinnacle1 style: #1F65AB */}
              <h6
                className="mb-3"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', fontWeight: 400, color: '#1F65AB' }}>
                
                <Link href="/services" style={{ color: '#1F65AB' }}>Strategic Services</Link>
              </h6>
              {/* H4 - Pinnacle1 style: weight 400, dark */}
              <h4
                className="mb-4"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '28px', fontWeight: 400, color: 'rgba(0,0,0,0.75)', lineHeight: 1.3 }}>
                
                IT Staffing &amp; Workforce Solutions Leadership
              </h4>
              <div style={{ width: '48px', height: '3px', background: '#00A7FF', marginBottom: '20px' }}></div>
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: '#48494B', lineHeight: 1.7, marginBottom: '24px' }}>
                Our highly customized staffing programs, leading technology partnerships, and significant investment in talent analytics deliver measurable outcomes for your workforce program. We align our strategies and metrics with your critical business goals to achieve high performance at scale.
              </p>
              <Link
                href="/services"
                className="learn-more-link inline-flex items-center gap-2"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 400, color: '#00A7FF' }}>
                
                Learn More
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_15ee5373f-1764645268112.png"
                alt="Business professionals collaborating in a modern office environment on IT staffing strategy"
                className="w-full object-cover"
                style={{ height: '380px' }} />
              
              <div
                className="absolute bottom-0 left-0 right-0 h-1"
                style={{ background: '#00A7FF' }} />
              
            </div>
          </div>

          {/* Payrolling Block - reversed */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div className="order-2 lg:order-1 relative">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_10d01e2d9-1767708000287.png"
                alt="Team of IT professionals working together on contract staffing and workforce solutions"
                className="w-full object-cover"
                style={{ height: '380px' }} />
              
              <div
                className="absolute bottom-0 left-0 right-0 h-1"
                style={{ background: '#00A7FF' }} />
              
            </div>
            <div className="order-1 lg:order-2">
              <h6
                className="mb-3"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1F65AB', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                
                Contract &amp; Payrolling Solutions
              </h6>
              <h4
                className="mb-4"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '28px', fontWeight: 400, color: 'rgba(0,0,0,0.75)', lineHeight: 1.3 }}>
                
                Employer/Agent-of-Record &amp; Contractor Compliance Leadership
              </h4>
              <div style={{ width: '48px', height: '3px', background: '#00A7FF', marginBottom: '20px' }}></div>
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: '#48494B', lineHeight: 1.7, marginBottom: '24px' }}>
                Increase cost savings, reduce attrition, and reduce risk. As your designated employer and/or agent of record, we provide your workforce with flexibility and outstanding benefits, while providing you with peace-of-mind. We fully indemnify our clients from potential misclassifications.
              </p>
              <Link
                href="/services"
                className="inline-flex items-center gap-2"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 400, color: '#00A7FF' }}>
                
                Learn More
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>

          {/* VMS Block */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h6
                className="mb-3"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1F65AB', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                
                Vendor Management System
              </h6>
              <h4
                className="mb-4"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '28px', fontWeight: 400, color: 'rgba(0,0,0,0.75)', lineHeight: 1.3 }}>
                
                Certified VMS Integrators for Leading Workforce Technology Platforms.
              </h4>
              <div style={{ width: '48px', height: '3px', background: '#00A7FF', marginBottom: '20px' }}></div>
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: '#48494B', lineHeight: 1.7, marginBottom: '24px' }}>
                Your VMS acts as the hub connecting your contingent workforce with internal systems and processes. A TranceformRIGHT-led approach to VMS design and implementation is holistic — including all systems and processes, from technology and operational process to supplier management and end-user adoption.
              </p>
              <Link
                href="/services"
                className="inline-flex items-center gap-2"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 400, color: '#00A7FF' }}>
                
                Learn More
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_18fa9154f-1785445433493.png"
                alt="Technology professionals implementing vendor management system and workforce technology solutions"
                className="w-full object-cover"
                style={{ height: '380px' }} />
              
              <div
                className="absolute bottom-0 left-0 right-0 h-1"
                style={{ background: '#00A7FF' }} />
              
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section - Pinnacle1 style */}
      <section className="section-padding" style={{ background: '#FAFAFA' }}>
        <div className="container-custom">
          {/* H3 - Pinnacle1 style: weight 100, white on dark or dark on light */}
          <h3
            className="mb-10"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '36px', fontWeight: 100, color: 'rgba(0,0,0,0.7)' }}>
            
            Case Studies
          </h3>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {caseStudies.map((study, i) =>
            <Link
              key={study.title}
              href="/case-studies"
              className="block group transition-all duration-300"
              style={{
                background: '#ffffff',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,145,221,0.15)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,167,255,0.3)';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                (e.currentTarget as HTMLElement).style.borderColor = '#e8e8e8';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
              }}>
              
                <div
                className="w-full"
                style={{
                  height: '200px',
                  background: i % 2 === 0 ?
                  'linear-gradient(135deg, #01142D 0%, #1F65AB 100%)' :
                  'linear-gradient(135deg, #0091DD 0%, #00A7FF 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                
                  <span
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '64px',
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.15)'
                  }}>
                  
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <div className="p-6">
                  <span
                  className="inline-block mb-3 px-3 py-1 text-xs font-semibold"
                  style={{
                    background: 'rgba(0,167,255,0.1)',
                    color: '#0091DD',
                    fontFamily: 'Montserrat, sans-serif'
                  }}>
                  
                    {study.industry}
                  </span>
                  <h4
                  className="mb-3 group-hover:text-blue-600 transition-colors duration-200"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 400, color: 'rgba(0,0,0,0.75)', lineHeight: 1.4 }}>
                  
                    {study.title}
                  </h4>
                  <p
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B', lineHeight: 1.6, marginBottom: '16px' }}>
                  
                    {study.excerpt}
                  </p>
                  <p
                  style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: 600, color: '#00A7FF' }}>
                  
                    {study.result}
                  </p>
                </div>
              </Link>
            )}
          </div>

          <div className="text-center">
            <Link
              href="/case-studies"
              className="inline-flex items-center gap-3 px-8 py-3.5 font-semibold transition-all duration-300"
              style={{ background: '#00A7FF', color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontSize: '14px' }}>
              
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              See all Case Studies
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section - Pinnacle1 style: white bg, thin Poppins heading, large stats */}
      <section className="section-padding" style={{ background: '#ffffff' }}>
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 'clamp(24px, 3vw, 40px)',
                  fontWeight: 100,
                  color: 'rgba(0,0,0,0.6)',
                  lineHeight: 1.4,
                  marginBottom: '24px'
                }}>
                
                We combine creative thinking, cutting-edge technology, and the technical know-how{' '}
                <strong style={{ fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}>
                  to deliver innovative workforce solutions for our clients.
                </strong>
              </h2>
              <div style={{ width: '48px', height: '3px', background: '#00A7FF', marginBottom: '24px' }}></div>
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: '#48494B', lineHeight: 1.7 }}>
                TranceformRIGHT is a leading provider of information technology staffing, Recruitment Process Outsourcing, and workforce solutions. We are one of the best in IT staffing and consulting services, providing staffing, MSP, and payrolling services across the United States.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, i) =>
              <div
                key={stat.label}
                className="p-8 text-center"
                style={{
                  background: i % 2 === 0 ? '#00A7FF' : '#01142D'
                }}>
                
                  <div
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '48px',
                    fontWeight: 200,
                    color: '#ffffff',
                    lineHeight: 1,
                    marginBottom: '8px'
                  }}>
                  
                    <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                  </div>
                  <div
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: 'rgba(255,255,255,0.8)',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase'
                  }}>
                  
                    {stat.label}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Current News Section - Pinnacle1 style */}
      <section className="section-padding" style={{ background: '#FAFAFA' }}>
        <div className="container-custom">
          <h2
            className="mb-10"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '36px', fontWeight: 100, color: 'rgba(0,0,0,0.6)' }}>
            
            Current News
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {newsItems.map((item, i) =>
            <article
              key={item.title}
              className="group transition-all duration-300"
              style={{
                background: '#ffffff',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,145,221,0.12)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,167,255,0.25)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                (e.currentTarget as HTMLElement).style.borderColor = '#e8e8e8';
              }}>
              
                <div
                style={{
                  height: '6px',
                  background: i % 2 === 0 ? '#00A7FF' : '#0091DD'
                }} />
              
                <div className="p-6">
                  <span
                  className="inline-block mb-3"
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: '#00A7FF'
                  }}>
                  
                    {item.category}
                  </span>
                  <h2
                  className="mb-3"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 400, color: 'rgba(0,0,0,0.8)', lineHeight: 1.4 }}>
                  
                    {item.title}
                  </h2>
                  <p
                  className="mb-4"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B', lineHeight: 1.6 }}>
                  
                    {item.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#888888' }}>
                    
                      {item.date}
                    </span>
                    <Link
                    href="/"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#00A7FF' }}>
                    
                      Read More →
                    </Link>
                  </div>
                </div>
              </article>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section - Pinnacle1 style: dark navy bg */}
      <section
        className="py-20"
        style={{ background: 'linear-gradient(135deg, #01142D 0%, #0A1F3D 100%)' }}>
        
        <div className="container-custom text-center">
          <h2
            className="mb-4"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 'clamp(28px, 4vw, 48px)',
              fontWeight: 200,
              color: '#ffffff',
              lineHeight: 1.3
            }}>
            
            Ready to Transform Your IT Workforce?
          </h2>
          <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto 24px' }}></div>
          <p
            className="mb-8 max-w-2xl mx-auto"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}>
            
            Connect with our team of expert IT staffing consultants and discover how TranceformRIGHT can help you build the technology team that drives your business forward.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/contact"
              className="inline-flex items-center justify-center px-10 py-4 font-semibold transition-all duration-300 hover:opacity-90"
              style={{ background: '#00A7FF', color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontSize: '15px' }}>
              
              Contact Us Today
            </Link>
            <Link
              href="/services"
              className="inline-flex items-center justify-center px-10 py-4 font-semibold transition-all duration-300"
              style={{ border: '2px solid rgba(255,255,255,0.4)', color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontSize: '15px' }}
              onMouseEnter={(e) => {(e.currentTarget as HTMLElement).style.borderColor = '#00A7FF';}}
              onMouseLeave={(e) => {(e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.4)';}}>
              
              View Our Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>);

}